import { useCallback, useState } from 'react';

import axios, { type AxiosRequestConfig } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';

import { type ChatHistoricalRecord, type DownloadChatOptions } from './components/Classes/types';
import { CLASS_INFO } from '../../config/apiPaths';
import { Status } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

type DownloadChatStatus = {
    [key: number]: Status;
};

interface DownloadChatParams {
    class_id: number;
    start_time: number;
    end_time: number;
    /* for verifying instructors of a class */
    class_uuid?: string;
}

const useDownloadChat = () => {
    const [getChatsStatus, setGetChatStatus] = useState<DownloadChatStatus>({});

    const { t } = useTranslation('admin_settings');
    const { api } = getUrls();
    const { enqueueSnackbar } = useSnackbar();

    const getChats = useCallback(
        async (options: DownloadChatOptions) => {
            setGetChatStatus((prevStatus) => ({
                ...prevStatus,
                [options.class_id]: Status.InProgress
            }));

            const {
                class_id: classId,
                class_uuid: classUuid,
                start_time: startTime,
                end_time: endTime,
                user_uuid: userUuid
            } = options;

            if (!classId || !startTime || !endTime) return;

            try {
                const params: DownloadChatParams = {
                    class_id: classId,
                    start_time: startTime,
                    end_time: endTime
                };

                if (classUuid) {
                    params.class_uuid = classUuid;
                }

                const config: AxiosRequestConfig = {
                    headers: getUserUuidHeaders(userUuid ?? ''),
                    params
                };

                const { data } = await axios.get<ChatHistoricalRecord[]>(`${api}${CLASS_INFO}/chats`, config);

                setGetChatStatus((prevStatus) => ({
                    ...prevStatus,
                    [options.class_id]: Status.Complete
                }));

                return data;
            } catch (err) {
                setGetChatStatus((prevStatus) => ({
                    ...prevStatus,
                    [options.class_id]: Status.Complete
                }));

                enqueueSnackbar(t('classes.downloadChatSomethingWentWrong'), { variant: 'error' });
            }
        },
        [api, enqueueSnackbar, t]
    );

    return {
        getChats,
        getChatsStatus
    };
};

const DownloadChatContainer = createContainer(useDownloadChat);

export default DownloadChatContainer;
